// src/utils/generateSpreadsheetUploadPdf.js
// Converts a student's uploaded .xlsx (Submission Mode: "Spreadsheet
// Upload" - see StudentDashboard.jsx and parseUploadedSpreadsheet.js) into a
// real vector PDF, one page per worksheet, via @react-pdf/renderer - mirrors
// generateExamPdf.js's own approach exactly, so the rest of the marking
// pipeline (FirebaseMarker.jsx's PDF viewer, createSubmission) never needs
// to know a submission didn't originate from a manually-uploaded PDF.

import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import { renderSpreadsheetGrid, renderSpreadsheetNote, spreadsheetGridLayout, columnsLabel, LANDSCAPE_GRID_WIDTH } from './generateExamPdf';
import { getExamInteractiveSpreadsheetMarking } from '../firebase/settings';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 11, fontFamily: 'Helvetica' },
  header: { marginBottom: 20, borderBottom: '2 solid #c33329', paddingBottom: 10 },
  headerTitle: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: '#c33329', marginBottom: 6 },
  headerLine: { fontSize: 10, color: '#444444', marginBottom: 2 },
  sectionHeading: { fontSize: 13, fontFamily: 'Helvetica-Bold', marginBottom: 10 }
});

function SpreadsheetUploadDocument({ studentName, cohortName, homeworkType, homeworkName, spreadsheetAnswers, pageOf }) {
  const sheetKeys = Object.keys(spreadsheetAnswers);

  // Spreadsheets marked as spreadsheets only: one page of "See Spreadsheet N"
  // notes instead of the grids (see prepareSpreadsheetUpload below)
  if (pageOf) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>SPREADSHEET SUBMISSION</Text>
            <Text style={styles.headerLine}>Student: {studentName}</Text>
            <Text style={styles.headerLine}>Homework: {homeworkName || homeworkType}</Text>
            <Text style={styles.headerLine}>Cohort: {cohortName}</Text>
          </View>
          {sheetKeys.map(sheetKey => renderSpreadsheetNote(
            spreadsheetAnswers[sheetKey].number,
            `Your worksheet "${spreadsheetAnswers[sheetKey].name}"`,
            sheetKey,
            pageOf
          ))}
        </Page>
      </Document>
    );
  }

  return (
    <Document>
      {sheetKeys.map((sheetKey, idx) => {
        const sheet = spreadsheetAnswers[sheetKey];
        return (
          <Page key={sheetKey} size="A4" orientation="landscape" style={styles.page}>
            {idx === 0 && (
              <View style={styles.header}>
                <Text style={styles.headerTitle}>SPREADSHEET SUBMISSION</Text>
                <Text style={styles.headerLine}>Student: {studentName}</Text>
                <Text style={styles.headerLine}>Homework: {homeworkName || homeworkType}</Text>
                <Text style={styles.headerLine}>Cohort: {cohortName}</Text>
              </View>
            )}
            <Text style={styles.sectionHeading}>{sheet.name}</Text>
            {(() => {
              // Already landscape; a sheet too wide even for that is split
              // into bands of columns (see spreadsheetGridLayout)
              const precomputedEval = sheet.precomputedEvalJson ? JSON.parse(sheet.precomputedEvalJson) : null;
              const layout = spreadsheetGridLayout(sheet.gridJson, precomputedEval);
              if (!layout) return null;
              return layout.bands.map((cols, bandIndex) => renderSpreadsheetGrid(
                sheet.gridJson,
                `${sheetKey}-band${bandIndex}`,
                layout.bands.length > 1 ? `${sheet.name}: columns ${columnsLabel(cols)}` : sheet.name,
                precomputedEval,
                { cols, gridWidth: LANDSCAPE_GRID_WIDTH }
              ));
            })()}
          </Page>
        );
      })}
    </Document>
  );
}

/**
 * Builds the uploaded workbook's PDF and wraps it as a real File - same
 * integration point generateExamPdf uses, since createSubmission() just
 * reads file.name and passes the file straight into uploadBytesResumable.
 */
export async function generateSpreadsheetUploadPdf({ studentName, cohortName, homeworkType, homeworkName, spreadsheetAnswers, pageOf }) {
  const blob = await pdf(
    <SpreadsheetUploadDocument
      studentName={studentName}
      cohortName={cohortName}
      homeworkType={homeworkType}
      homeworkName={homeworkName}
      spreadsheetAnswers={spreadsheetAnswers}
      pageOf={pageOf}
    />
  ).toBlob();

  return new File([blob], `${homeworkType}_spreadsheet.pdf`, { type: 'application/pdf' });
}

/**
 * The PDF and spreadsheetAnswers for an uploaded workbook, following the
 * homework type's Interactive Spreadsheet Marking setting: "Spreadsheet
 * only" gives a page of "See Spreadsheet N" notes, recording which page of
 * this file each note is on (pdfPage) and the file's name (pdfFileName -
 * FirebaseMarker.jsx joins a submission's files into one PDF, so the marked
 * PDF needs to know where this one starts). Any other setting keeps what
 * uploads have always done: the grids in the PDF, and marking on the
 * spreadsheet too.
 */
export async function prepareSpreadsheetUpload({ studentName, cohortName, homeworkType, homeworkName, spreadsheetAnswers }) {
  const numbered = {};
  Object.entries(spreadsheetAnswers).forEach(([key, sheet], i) => { numbered[key] = { ...sheet, number: i + 1 }; });
  const setting = await getExamInteractiveSpreadsheetMarking();
  const spreadsheetMarkingMode = setting?.[homeworkType] === 'spreadsheet' ? 'spreadsheet' : 'both';
  const pageOf = spreadsheetMarkingMode === 'spreadsheet' ? {} : undefined;
  const file = await generateSpreadsheetUploadPdf({ studentName, cohortName, homeworkType, homeworkName, spreadsheetAnswers: numbered, pageOf });
  if (pageOf) {
    Object.entries(numbered).forEach(([key, sheet]) => {
      if (pageOf[key]) sheet.pdfPage = pageOf[key];
      sheet.pdfFileName = file.name;
    });
  }
  return { file, spreadsheetAnswers: numbered, spreadsheetMarkingMode };
}
